/**
 * Policy DSL + evaluation + override domain types.
 *
 * SOURCE OF TRUTH: `docs/decisions/business/ADR-103-business-policy-enforcement.md`
 *   §1  DSL v1.0 document shape
 *   §2  Expression language
 *   §3  Storage schema
 *   §7  Lifecycle (draft → shadow → active → deprecated → archived)
 *   §8  Conflict resolution (priority)
 *
 * This frontend mirror is kept in parity with the backend DSL via the
 * (future) CI gate `business-policy-dsl-parity`.
 *
 * MVP cut: we ship a typed shape for *metadata + scope + onDeny + audit*
 * which is everything the Admin UI needs to display/edit. The full
 * expression tree is kept as a loosely-typed JSON value — the backend
 * evaluator owns strict schema validation once the API lands.
 */
import { z } from 'zod';

export const POLICY_STATUSES = [
  'draft',
  'shadow',
  'active',
  'deprecated',
  'archived',
] as const;
export type PolicyStatus = (typeof POLICY_STATUSES)[number];

export const POLICY_AUDIT_LEVELS = ['L0', 'L1', 'L2', 'L3'] as const;
export type PolicyAuditLevel = (typeof POLICY_AUDIT_LEVELS)[number];

export const POLICY_AUDIT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type PolicyAuditSeverity = (typeof POLICY_AUDIT_SEVERITIES)[number];

export const POLICY_APPLY_TO = ['all_tenants', 'tier_gated', 'explicit_list'] as const;
export type PolicyApplyTo = (typeof POLICY_APPLY_TO)[number];

export const POLICY_COMBINATORS = ['all', 'any'] as const;
export type PolicyCombinator = (typeof POLICY_COMBINATORS)[number];

export const POLICY_OVERRIDE_MODES = [
  'disable',
  'force_allow',
  'stricter',
] as const;
export type PolicyOverrideMode = (typeof POLICY_OVERRIDE_MODES)[number];

/**
 * Scope — which sub-verticals / actions / tenants the policy matches.
 */
export const policyScopeSchema = z.object({
  subVerticalKeys: z.array(z.string().min(1)).default([]),
  actions: z.array(z.string().regex(/^[a-z_]+\.[a-z_*]+$/)).min(1),
  applyTo: z.enum(POLICY_APPLY_TO).default('all_tenants'),
  tenantList: z.array(z.string().uuid()).optional(),
});
export type PolicyScope = z.infer<typeof policyScopeSchema>;

/**
 * onDeny — RFC 7807-friendly deny response contract.
 */
export const policyOnDenySchema = z.object({
  statusCode: z.number().int().min(400).max(599).default(403),
  errorCode: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
  messageKey: z.string().optional(),
  message: z.string().min(1),
});
export type PolicyOnDeny = z.infer<typeof policyOnDenySchema>;

/**
 * Audit config — per-policy level/severity/logging behaviour.
 */
export const policyAuditSchema = z.object({
  level: z.enum(POLICY_AUDIT_LEVELS).default('L2'),
  severity: z.enum(POLICY_AUDIT_SEVERITIES).default('warning'),
  logDenial: z.boolean().default(true),
  logAllow: z.boolean().default(false),
});
export type PolicyAuditConfig = z.infer<typeof policyAuditSchema>;

/**
 * A single condition (readable wrapper around the expression tree).
 * For MVP we keep the expression as untyped JSON — the backend owns it.
 */
export const policyConditionSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  description: z.string().min(1),
  expression: z.unknown(),
});
export type PolicyCondition = z.infer<typeof policyConditionSchema>;

/**
 * Full Policy DSL document (v1.0).
 */
export const policyDocumentSchema = z.object({
  policyId: z.string().regex(/^POL-[A-Z0-9_-]+$/, {
    message: 'policyId must match POL-XXX format',
  }),
  version: z.string().regex(/^\d+\.\d+\.\d+$/, { message: 'version must be semver' }),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  scope: policyScopeSchema,
  conditions: z.array(policyConditionSchema).min(1),
  combinator: z.enum(POLICY_COMBINATORS).default('all'),
  priority: z.number().int().min(0).max(1000).default(100),
  onDeny: policyOnDenySchema,
  onAllow: z.object({ audit: z.boolean().default(false) }).optional(),
  audit: policyAuditSchema,
  metadata: z
    .object({
      createdBy: z.string().optional(),
      createdAt: z.string().optional(),
      tags: z.array(z.string()).default([]),
      relatedRegulation: z.string().optional(),
    })
    .optional(),
  status: z.enum(POLICY_STATUSES).default('draft'),
});
export type PolicyDocument = z.infer<typeof policyDocumentSchema>;

/**
 * Row shape used by the admin UI (flattens a few fields for cheap filtering).
 */
export interface PolicyRecord {
  id: string;
  policyId: string;
  version: string;
  name: string;
  description?: string;
  status: PolicyStatus;
  scopeActions: string[];
  scopeVerticalKeys: string[];
  priority: number;
  combinator: PolicyCombinator;
  audit: PolicyAuditConfig;
  onDeny: PolicyOnDeny;
  document: PolicyDocument;
  createdAt: string;
  updatedAt: string;
  activatedAt?: string;
  deprecatedAt?: string;
  checksumSha256: string;
}

/**
 * Lifecycle transition form contract (shadow/activate/deprecate/archive).
 *
 * Every transition REQUIRES a reason per ECTP Rule 7.
 */
export const POLICY_LIFECYCLE_ACTIONS = [
  'shadow',
  'activate',
  'deprecate',
  'archive',
] as const;
export type PolicyLifecycleAction = (typeof POLICY_LIFECYCLE_ACTIONS)[number];

export const policyLifecycleFormSchema = z.object({
  action: z.enum(POLICY_LIFECYCLE_ACTIONS),
  reason: z.string().min(10).max(500),
});
export type PolicyLifecycleFormValues = z.infer<typeof policyLifecycleFormSchema>;

/**
 * Tenant-specific override (nadiren kullanılır, quarterly review'a tabi).
 */
export interface PolicyTenantOverride {
  id: string;
  tenantId: string;
  tenantName: string;
  policyId: string;
  override: PolicyOverrideMode;
  reason: string;
  createdAt: string;
  createdBy: string;
  expiresAt?: string;
}

export const policyOverrideFormSchema = z.object({
  tenantId: z.string().uuid({ message: 'tenantRequired' }),
  override: z.enum(POLICY_OVERRIDE_MODES),
  reason: z.string().min(10).max(500),
  expiresAt: z.string().optional(),
});
export type PolicyOverrideFormValues = z.infer<typeof policyOverrideFormSchema>;

/**
 * Historical evaluation row (last-N shown in the UI for drilldown).
 */
export interface PolicyEvaluationRecord {
  id: string;
  policyId: string;
  tenantId: string;
  tenantName: string;
  userId: string;
  action: string;
  result: 'allow' | 'deny' | 'error';
  errorMessage?: string;
  durationMs: number;
  correlationId: string;
  evaluatedAt: string;
}

/**
 * Simulator contract — what the operator types in + what the engine returns.
 *
 * Context is a free-form JSON object: profile/tenant/user/payload keys
 * expected by the specific policy. We validate it's valid JSON (not a
 * specific shape) on the client; the server-side evaluator will type-check.
 */
export const simulatorInputSchema = z.object({
  contextJson: z.string().min(2).superRefine((raw, ctx) => {
    try {
      JSON.parse(raw);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'invalidJson' });
    }
  }),
});
export type SimulatorInputValues = z.infer<typeof simulatorInputSchema>;

export interface SimulatorConditionTrace {
  conditionId: string;
  description: string;
  outcome: 'pass' | 'fail' | 'error';
  explanation?: string;
  durationMs: number;
}

export interface SimulatorResult {
  policyId: string;
  outcome: 'allow' | 'deny' | 'error';
  statusCode?: number;
  errorCode?: string;
  message?: string;
  durationMs: number;
  trace: SimulatorConditionTrace[];
  simulatedAt: string;
}
